'use client'

import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { CreditCard, ShieldAlert } from 'lucide-react'
import { z } from 'zod/v4'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  Input,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { schoolGradeLabel } from '@helper/utils/domain'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { Student } from '../types'
import { useSharedTRPC } from '../../../context'
import { useStudentMutations } from '../use-student-mutations'

const FormSchema = z.object({
  parentEmail: z.email({ message: 'Format email tidak valid.' }).min(1, { message: 'Wajib isi.' }),
})

interface RequestHomeConnectDialogProps {
  student: Student
  onClose: () => void
}

/** The two things a school is promising the parent, spelled out before they hand over an address. */
const BENEFITS = [
  {
    Icon: ShieldAlert,
    text: 'Orang tua dapat melihat kemajuan dan aktivitas belajar, pembelajaran berlanjut tanpa hambatan antara rumah dan sekolah.',
  },
  {
    Icon: CreditCard,
    text: 'Akses premium yang fleksibel, dapat didanai oleh sekolah atau orang tua.',
  },
] as const

/**
 * Opens a Sambung Sekolah-Rumah request for one student.
 *
 * The school types an address and nothing more — the parent's name is unknown until they accept, and
 * the product holds no other contact detail for them. Everything about who the student is comes from
 * the roster row.
 */
export function RequestHomeConnectDialog({ student, onClose }: RequestHomeConnectDialogProps) {
  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  const request = useMutation(trpc.homeConnect.request.mutationOptions())

  const form = useForm({
    defaultValues: { parentEmail: '' } as z.input<typeof FormSchema>,
    validators: { onSubmit: FormSchema },
    onSubmit: async ({ value }) => {
      try {
        await request.mutateAsync({ childId: student.id, parentEmail: value.parentEmail })
        await invalidate()
        toast.success('Permintaan berhasil terkirim.')
        onClose()
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Gagal mengirim permohonan')
      }
    },
  })

  const gradeLabel = student.schoolGrade ? schoolGradeLabel(student.schoolGrade) : null
  const className = student.schoolClass?.name ?? null
  const subtitle = [gradeLabel, className].filter(Boolean).join(' • ')

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className='srd:flex srd:max-h-[95dvh] srd:w-full srd:max-w-lg srd:flex-col srd:overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Sambung Sekolah-Rumah</DialogTitle>
          <DialogDescription>
            Kirim permohonan sambung Sekolah-Rumah agar orang tua dapat mendukung pembelajaran murid di rumah.
          </DialogDescription>
        </DialogHeader>

        <ul className='srd:flex srd:flex-col srd:gap-3'>
          {BENEFITS.map(({ Icon, text }) => (
            <li key={text} className='srd:flex srd:items-start srd:gap-x-3'>
              <Icon className='srd:text-neutral-black srd:mt-0.5 srd:h-4 srd:w-4 srd:shrink-0' />
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>{text}</span>
            </li>
          ))}
        </ul>

        <div className='srd:flex srd:flex-nowrap srd:items-center srd:gap-x-3'>
          <Avatar profile='user' size='lg'>
            {student.image && <AvatarImage src={student.image} alt={student.name} />}
            <AvatarFallback>{getInitials(student.name)}</AvatarFallback>
          </Avatar>
          <span className='srd:flex srd:flex-col'>
            <span className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>
              {student.name}
            </span>
            {subtitle && (
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>{subtitle}</span>
            )}
          </span>
        </div>

        <form
          className='srd:flex srd:flex-col srd:gap-4'
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <FieldSet disabled={request.isPending}>
            <form.Field name='parentEmail'>
              {(field) => (
                <Field>
                  <FieldLabel>Email orang tua</FieldLabel>
                  <Input
                    type='email'
                    autoComplete='email'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )}
            </form.Field>
          </FieldSet>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose} disabled={request.isPending}>
              Kembali
            </Button>
            <Button type='submit' loading={request.isPending} disabled={request.isPending}>
              Kirim
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
